import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FileStack } from 'lucide-react';
import Dialog from '../ui/Dialog';
import Field from '../ui/Field';
import EmptyState from '../EmptyState';
import ForwardLink from '../details/ForwardLink';
import { ServerError, SubmitButton } from './formBits';
import { api } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';
import { CHANGEABLE_STATUSES, CORE_PHASES, scheduleKey, needsWarrantyOnStatus, warrantyMissing, warrantyOf, warrantyPatch, type WarrantyPatch } from './projectModel';
import WarrantyField from './WarrantyField';
import type { CorePhase, ProjectFinancials } from '../../types';

/**
 * P-04/C — "פרטים כספיים": read-only. Price, monthly server fee, payment
 * terms and the payment schedule with its amounts ("תשלום 1 מתוך 3: 40,000 ₪"),
 * and "עריכה" for who may change them (GM / Account Manager).
 */
export function ProjectFinancialsDialog({ kind, financials, canEdit, onEdit, onClose }: {
  kind: string;
  financials: ProjectFinancials;
  canEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const money = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '—' : formatCurrency(Number(v)));
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('projectPage.financials.title')}
      size="md"
      footer={(
        <div className="flex w-full items-center justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.close')}</button>
          {canEdit && <button type="button" className="btn btn-primary" onClick={onEdit}>{t('projectPage.financials.edit')}</button>}
        </div>
      )}
    >
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
        <div>
          <dt className="text-xs text-surface-500">{t('projectForm.price')}</dt>
          <dd className="mt-0.5 text-sm text-surface-900 tabular-nums">{money(financials.fixed_price)}</dd>
        </div>
        <div>
          <dt className="text-xs text-surface-500">{t('projectForm.serverFee')}</dt>
          <dd className="mt-0.5 text-sm text-surface-900 tabular-nums">{money(financials.server_monthly_fee)}</dd>
        </div>
        <div>
          <dt className="text-xs text-surface-500">{t('projectForm.serverCost')}</dt>
          <dd className="mt-0.5 text-sm text-surface-900 tabular-nums">{money(financials.server_monthly_cost)}</dd>
        </div>
        <div>
          <dt className="text-xs text-surface-500">{t('projectForm.paymentTerms')}</dt>
          <dd className="mt-0.5 text-sm text-surface-900">{financials.payment_terms ? t(`paymentTerms.${financials.payment_terms}`) : '—'}</dd>
        </div>
      </dl>
      <div className="mt-5">
        <h3 className="text-sm font-semibold text-surface-800">{t('projectForm.schedule')}</h3>
        <p className="mt-0.5 text-xs text-surface-600">{t(scheduleKey(kind))} {t('projectForm.scheduleFixed')}</p>
        <ul className="mt-2 divide-y divide-surface-100 rounded-lg border border-surface-200">
          {financials.payment_schedule.map((p) => (
            <li key={p.index} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="text-surface-800">{t('projectPage.financials.paymentOf', { index: p.index, of: p.of })} · {p.percent}%</span>
              <span className="tabular-nums text-surface-900">{p.amount === null ? '—' : formatCurrency(p.amount)}</span>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}

/**
 * P-04/C — "מסמכים": the documents linked to this project (or to its
 * engagement) — the existing document-link mechanism — and the way to the
 * customer's documents folder.
 */
export function ProjectDocumentsDialog({ projectId, engagementId, organizationId, onClose }: {
  projectId: string;
  engagementId: string | null;
  organizationId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['project-documents', projectId, engagementId],
    queryFn: async () => {
      const [byProject, byEngagement] = await Promise.all([
        api.listDocumentsByEntity('project', projectId),
        engagementId ? api.listDocumentsByEntity('engagement', engagementId) : Promise.resolve({ data: [] } as any),
      ]);
      const seen = new Set<string>();
      return [...(byProject.data ?? []), ...(byEngagement.data ?? [])].filter((d: any) => (seen.has(d.id) ? false : (seen.add(d.id), true)));
    },
  });
  const docs = data ?? [];
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('projectPage.documents.title')}
      size="md"
      footer={(
        <div className="flex w-full items-center justify-between gap-2">
          <ForwardLink to={`/documents?org=${organizationId}`}>{t('projectPage.documents.openFolder')}</ForwardLink>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.close')}</button>
        </div>
      )}
    >
      {isLoading ? (
        <p className="text-sm text-surface-600">{t('common.loading')}</p>
      ) : docs.length === 0 ? (
        <EmptyState icon={FileStack} title={t('projectPage.documents.empty')} description={t('projectPage.documents.emptyHint')} />
      ) : (
        <ul className="divide-y divide-surface-100">
          {docs.map((d: any) => (
            <li key={d.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <ForwardLink to={`/documents?org=${organizationId}&doc=${d.id}`}>{d.title}</ForwardLink>
              <span className="text-xs text-surface-500 tabular-nums">{formatDate(d.updated_at ?? d.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

/** P-04/C — "שינוי סטטוס": בתכנון / פעיל / מושהה / הושלם ("בוטל" is "ביטול הפרויקט"). */
export function ProjectStatusDialog({ current, kind, warranty, initialStatus, pending, error, onConfirm, onClose }: {
  current: string;
  /** D-303: 'project' | 'development' - ending one asks for "אחריות ללקוח". */
  kind?: string | null;
  /** D-303: the row as it is (warranty_until / no_warranty), the start of the warranty question. */
  warranty?: { warranty_until?: unknown; no_warranty?: unknown } | null;
  /** D-303: the status picked on the status pill, preselected. */
  initialStatus?: string;
  pending: boolean;
  error: unknown;
  onConfirm: (status: string, warranty?: WarrantyPatch) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>(
    initialStatus && (CHANGEABLE_STATUSES as readonly string[]).includes(initialStatus) ? initialStatus
      : (CHANGEABLE_STATUSES as readonly string[]).includes(current) ? current : 'planning'
  );
  const [warrantyValue, setWarrantyValue] = useState(() => warrantyOf(warranty));
  const [warrantyError, setWarrantyError] = useState('');
  const askWarranty = needsWarrantyOnStatus(kind, current, status);
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('projectPage.status.title')}
      size="sm"
      dismissible={!pending}
      footer={(
        <div className="flex w-full items-center justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={pending}>{t('common.cancel')}</button>
          <SubmitButton busy={pending} form="project-status-form" disabled={status === current}>{t('common.save')}</SubmitButton>
        </div>
      )}
    >
      <form
        id="project-status-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (askWarranty) {
            if (warrantyMissing(warrantyValue)) { setWarrantyError(t('warranty.required')); return; }
            onConfirm(status, warrantyPatch(warrantyValue));
            return;
          }
          onConfirm(status);
        }}
        className="space-y-3"
      >
        <ServerError error={error} fallback={t('project.actionFailed')} />
        <Field id="project-status" label={t('project.status')}>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {CHANGEABLE_STATUSES.map((s) => <option key={s} value={s}>{t(`projectStatus.${s}`)}</option>)}
          </select>
        </Field>
        {askWarranty && (
          <WarrantyField
            id="project-status-warranty"
            value={warrantyValue}
            onChange={(v) => { setWarrantyValue(v); setWarrantyError(''); }}
            error={warrantyError || undefined}
            required
            hint={t('warranty.intro')}
          />
        )}
        <p className="text-xs text-surface-600">{t('projectPage.status.cancelHint')}</p>
      </form>
    </Dialog>
  );
}

/** P-04/A2 — "שינוי שלב": set the phase by hand (back to automatic is its own menu action). */
export function ProjectPhaseDialog({ current, auto, pending, error, onConfirm, onClose }: {
  current: CorePhase | null;
  auto: CorePhase | null;
  pending: boolean;
  error: unknown;
  onConfirm: (phase: CorePhase) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<CorePhase>(current ?? auto ?? 'development');
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('projectPage.phase.title')}
      size="sm"
      dismissible={!pending}
      footer={(
        <div className="flex w-full items-center justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={pending}>{t('common.cancel')}</button>
          <SubmitButton busy={pending} form="project-phase-form">{t('common.save')}</SubmitButton>
        </div>
      )}
    >
      <form id="project-phase-form" onSubmit={(e) => { e.preventDefault(); onConfirm(phase); }} className="space-y-3">
        <ServerError error={error} fallback={t('project.actionFailed')} />
        <Field id="project-phase" label={t('projectPage.phase.label')}
          hint={auto ? t('projectPage.phase.autoHint', { phase: t(`projectPhase.${auto}`) }) : t('projectPage.phase.autoNone')}>
          <select className="input" value={phase} onChange={(e) => setPhase(e.target.value as CorePhase)}>
            {CORE_PHASES.map((p) => <option key={p} value={p}>{t(`projectPhase.${p}`)}</option>)}
          </select>
        </Field>
      </form>
    </Dialog>
  );
}
